import React, { useRef, useState, useCallback } from 'react';
import Icon from '@omilia/icon';
import { colors } from '@omilia/theme';
import Tooltip from '@omilia/tooltip';
import { DropdownTrigger, Dropdown } from 'react-simple-dropdown';
import { connect } from 'react-redux';

import {
  StyledDropdownContentHeader,
  StyledDropdownContentHeaderTitle,
  StyledDropdownContentBody,
  StyledHeaderMenuItemLabel,
  StyledMenuItem,
} from 'components/HeaderDropdownMenu/styled';
import { PartialRootState } from 'store/services/versionControl/types';
import {
  getProjectSlug,
  getProjectAccount,
  getProjectBranch,
  // @ts-ignore
} from 'store/services/project/selectors';
import { ActionType } from 'hooks/useVersionControl/types';
import { SDropdown, SDropdownContent, SDivider } from './styled';
import VersionControlActivation from './components/VersionControlActivation';
import CommitAndFetchModal from './components/CommitAndFetchModal';
import MergeBranchModal from './components/MergeBranchModal';
import { Props } from './types';

const VersionControlMenu = ({
  projectBranch,
  projectName,
  projectAccount,
  onPushToHistory,
}: Props) => {
  const dropdownRef = useRef<React.RefObject<typeof Dropdown>>(null);
  const [isCommitAndFetchModalOpen, setIsCommitAndFetchModalOpen] = useState(
    false,
  );
  const [modalAction, setModalAction] = useState<ActionType>('commit');
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

  const closeVersionControlMenu = () => {
    // @ts-ignore
    dropdownRef.current.hide();
  };

  const openCommitAndFetchModal = (action: ActionType) => {
    closeVersionControlMenu();
    setModalAction(action);
    setIsCommitAndFetchModalOpen(true);
  };

  const openMergeModal = () => {
    closeVersionControlMenu();
    setIsMergeModalOpen(true);
  };

  const closeCommitAndFetchModal = () => setIsCommitAndFetchModalOpen(false);

  const closeMergeModal = () => setIsMergeModalOpen(false);

  const onEnableVersionControl = useCallback(
    () =>
      onPushToHistory(
        `/${projectAccount}/${projectName}/project-settings/version-control`,
      ),
    [onPushToHistory, projectAccount, projectName],
  );

  return (
    <>
      <SDropdown ref={dropdownRef} disabled={false}>
        <DropdownTrigger>
          <Tooltip content="GitLab Synchronization" placement="left">
            <Icon
              glyph="git-logo"
              primaryColor={colors.COLOR_OMILIA_BLUE_200}
              size="16"
            />
          </Tooltip>
        </DropdownTrigger>
        <SDropdownContent isEnabled={!!projectBranch}>
          {projectBranch ? (
            <>
              <StyledDropdownContentHeader>
                <StyledDropdownContentHeaderTitle>
                  GitLab Synchronization
                </StyledDropdownContentHeaderTitle>
              </StyledDropdownContentHeader>

              <StyledDropdownContentBody data-test="version-control-menu">
                <Tooltip
                  content="Commit &amp; Push your changes"
                  placement="left"
                >
                  <StyledMenuItem
                    as="a"
                    onClick={() => openCommitAndFetchModal('commit')}
                  >
                    <Icon
                      glyph="commit"
                      primaryColor={colors.COLOR_OMILIA_BLUE_600}
                      size="16"
                    />
                    <StyledHeaderMenuItemLabel>
                      Commit &amp; Push
                    </StyledHeaderMenuItemLabel>
                  </StyledMenuItem>
                </Tooltip>
                <Tooltip content="Pulling the current branch" placement="left">
                  <StyledMenuItem
                    as="a"
                    onClick={() => openCommitAndFetchModal('pull')}
                  >
                    <Icon
                      glyph="fetch"
                      primaryColor={colors.COLOR_OMILIA_BLUE_600}
                      size="16"
                    />
                    <StyledHeaderMenuItemLabel>Fetch</StyledHeaderMenuItemLabel>
                  </StyledMenuItem>
                </Tooltip>
                <SDivider />
                <Tooltip
                  content="Merge current branch into its parent branch"
                  placement="left"
                >
                  <StyledMenuItem as="a" onClick={openMergeModal}>
                    <Icon
                      glyph="merge"
                      primaryColor={colors.COLOR_OMILIA_BLUE_600}
                      size="16"
                    />
                    <StyledHeaderMenuItemLabel>
                      Merge Branch
                    </StyledHeaderMenuItemLabel>
                  </StyledMenuItem>
                </Tooltip>
              </StyledDropdownContentBody>
            </>
          ) : (
            <VersionControlActivation onClick={onEnableVersionControl} />
          )}
        </SDropdownContent>
      </SDropdown>
      {isCommitAndFetchModalOpen && (
        <CommitAndFetchModal
          selectedAction={modalAction}
          onCancel={closeCommitAndFetchModal}
        />
      )}
      {isMergeModalOpen && <MergeBranchModal onCancel={closeMergeModal} />}
    </>
  );
};

const mapStateToProps = (state: PartialRootState, props: Props) => ({
  ...props,
  projectBranch: getProjectBranch(state),
  projectName: getProjectSlug(state),
  projectAccount: getProjectAccount(state),
});

export default connect(mapStateToProps)(VersionControlMenu);
